import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Text, Box, Switch, useToast,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb, Badge, Divider, Flex,
} from '@chakra-ui/react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

// Master column definitions
export const COLUMN_DEFS = [
  // Project-level columns
  { key: 'drag', label: 'Drag Handle', level: 'system', defaultVisible: true, defaultWidth: 30, fixed: true },
  { key: 'done', label: 'Done', level: 'project', defaultVisible: true, defaultWidth: 35, fixed: true },
  { key: 'wc', label: 'Work Centre', level: 'project', defaultVisible: true, defaultWidth: 40 },
  { key: 'project', label: 'Project Name', level: 'project', defaultVisible: true, defaultWidth: 260, fixed: true },
  { key: 'project_date', label: 'Project Date', level: 'project', defaultVisible: false, defaultWidth: 80 },
  { key: 'deadline', label: 'Project Deadline', level: 'project', defaultVisible: false, defaultWidth: 85 },
  { key: 'client', label: 'Client (Company)', level: 'project', defaultVisible: true, defaultWidth: 150 },
  { key: 'contact', label: 'Contact', level: 'project', defaultVisible: true, defaultWidth: 120 },
  { key: 'action_type', label: 'Type of Work', level: 'project', defaultVisible: true, defaultWidth: 110 },
  // Task-level columns
  { key: 'task', label: 'Task Name', level: 'task', defaultVisible: true, defaultWidth: 140, minW: true, fixed: true },
  { key: 'task_deadline', label: 'Task Deadline', level: 'task', defaultVisible: false, defaultWidth: 80 },
  { key: 'task_companies', label: 'Task Companies', level: 'task', defaultVisible: false, defaultWidth: 120 },
  { key: 'task_contacts', label: 'Task Contacts', level: 'task', defaultVisible: false, defaultWidth: 120 },
  { key: 'held', label: 'Held', level: 'task', defaultVisible: true, defaultWidth: 30 },
  { key: 'complete', label: 'Task Complete', level: 'task', defaultVisible: true, defaultWidth: 30 },
  { key: 'calendar', label: 'Calendar', level: 'task', defaultVisible: true, defaultWidth: 26 },
  { key: 'bookings', label: 'Bookings', level: 'task', defaultVisible: true, defaultWidth: 26 },
  { key: 'planned_visits', label: 'Planned Visits', level: 'task', defaultVisible: true, defaultWidth: 26 },
  { key: 'diary', label: 'Request Appointment', level: 'task', defaultVisible: true, defaultWidth: 26 },
  { key: 'progress', label: 'Progress', level: 'task', defaultVisible: true, defaultWidth: 24 },
  { key: 'clock', label: 'Clock On', level: 'task', defaultVisible: true, defaultWidth: 22 },
  { key: 'mileage', label: 'Mileage', level: 'task', defaultVisible: true, defaultWidth: 24 },
  { key: 'docs', label: 'Documents', level: 'task', defaultVisible: true, defaultWidth: 30 },
  { key: 'procedures', label: 'Procedures', level: 'task', defaultVisible: true, defaultWidth: 30 },
  { key: 'passwords', label: 'Password Safe', level: 'task', defaultVisible: true, defaultWidth: 30 },
  { key: 'uploads', label: 'Client Uploads', level: 'task', defaultVisible: true, defaultWidth: 30 },
  { key: 'incidents', label: 'Incidents', level: 'task', defaultVisible: true, defaultWidth: 30 },
  { key: 'feedback', label: 'Feedback', level: 'task', defaultVisible: true, defaultWidth: 30 },
  { key: 'comms', label: 'Communications', level: 'task', defaultVisible: true, defaultWidth: 30 },
  { key: 'notes', label: 'Notes', level: 'task', defaultVisible: true, defaultWidth: 30 },
  { key: 'suppliers', label: 'Suppliers', level: 'task', defaultVisible: true, defaultWidth: 28 },
  { key: 'prospect', label: 'Prospect Edit', level: 'task', defaultVisible: true, defaultWidth: 24 },
  { key: 'inv_in',  label: '£ IN',    level: 'finance', defaultVisible: true, defaultWidth: 60 },
  { key: 'inv_out', label: '£ OUT',   level: 'finance', defaultVisible: true, defaultWidth: 60 },
  { key: 'vat',     label: 'VAT',     level: 'finance', defaultVisible: true, defaultWidth: 65 },
  { key: 'bal',     label: 'Balance', level: 'finance', defaultVisible: true, defaultWidth: 65 },
  { key: 'delete', label: 'Delete', level: 'system', defaultVisible: false, defaultWidth: 30, fixed: true },
];

export function getColumnConfig(userConfig) {
  const config = {};
  for (const def of COLUMN_DEFS) {
    const userOverride = userConfig?.[def.key] || {};
    config[def.key] = {
      visible: userOverride.visible !== undefined ? userOverride.visible : def.defaultVisible,
      width: userOverride.width || def.defaultWidth,
    };
  }
  return config;
}

export function isColVisible(config, key) {
  return config[key]?.visible !== false;
}

export function colWidth(config, key) {
  const def = COLUMN_DEFS.find(d => d.key === key);
  return config[key]?.width || def?.defaultWidth || 30;
}

export default function DashboardColumnsModal({ isOpen, onClose }) {
  const { user, refreshUser } = useAuth();
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      const userCfg = user?.dashboard_config || {};
      const cfg = {};
      for (const def of COLUMN_DEFS) {
        const uo = userCfg[def.key] || {};
        cfg[def.key] = {
          visible: uo.visible !== undefined ? uo.visible : def.defaultVisible,
          width: uo.width || def.defaultWidth,
        };
      }
      setConfig(cfg);
    }
  }, [isOpen, user]);

  const toggle = (key) => {
    const def = COLUMN_DEFS.find(d => d.key === key);
    if (def?.fixed) return;
    setConfig(prev => ({ ...prev, [key]: { ...prev[key], visible: !prev[key]?.visible } }));
  };

  const setWidth = (key, w) => {
    setConfig(prev => ({ ...prev, [key]: { ...prev[key], width: w } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/auth/preferences', { dashboard_config: config });
      if (refreshUser) refreshUser();
      else {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        u.dashboard_config = config;
        localStorage.setItem('user', JSON.stringify(u));
      }
      toast({ title: 'Dashboard settings saved', status: 'success', duration: 1500 });
      onClose();
      window.location.reload();
    } catch(e) { toast({ title: 'Failed to save', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  const resetDefaults = () => {
    const cfg = {};
    for (const def of COLUMN_DEFS) {
      cfg[def.key] = { visible: def.defaultVisible, width: def.defaultWidth };
    }
    setConfig(cfg);
  };

  const projectCols = COLUMN_DEFS.filter(d => d.level === 'project');
  const taskCols = COLUMN_DEFS.filter(d => d.level === 'task');
  const financeCols = COLUMN_DEFS.filter(d => d.level === 'finance');

  const renderCol = (def) => {
    const c = config[def.key] || {};
    const vis = c.visible !== false;
    return (
      <Box key={def.key} p={2} borderRadius="md" bg={vis ? 'white' : 'gray.50'}
        border="1px" borderColor={vis ? 'gray.200' : 'gray.100'}>
        <Flex justify="space-between" align="center" mb={vis ? 1 : 0}>
          <HStack spacing={2}>
            {vis ? <FiEye size={12} color="#38A169" /> : <FiEyeOff size={12} color="#A0AEC0" />}
            <Text fontSize="12px" fontWeight={vis ? '600' : '400'} color={vis ? 'gray.800' : 'gray.400'}>
              {def.label}
            </Text>
            {def.fixed && <Badge fontSize="8px" colorScheme="gray">Required</Badge>}
          </HStack>
          {!def.fixed && <Switch size="sm" isChecked={vis} onChange={() => toggle(def.key)} colorScheme="green" />}
        </Flex>
        {vis && !def.fixed && (
          <Flex align="center" gap={2} mt={1}>
            <Text fontSize="9px" color="gray.400" w="40px">Width</Text>
            <Slider min={20} max={300} step={5} value={c.width || def.defaultWidth}
              onChange={(v) => setWidth(def.key, v)} colorScheme="blue" flex={1} size="sm">
              <SliderTrack h="4px"><SliderFilledTrack /></SliderTrack>
              <SliderThumb boxSize={3} />
            </Slider>
            <Badge fontSize="9px" colorScheme="blue" minW="40px" textAlign="center">{c.width || def.defaultWidth}px</Badge>
          </Flex>
        )}
      </Box>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">Master Dashboard Settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase">Project Columns</Text>
            {projectCols.map(renderCol)}

            <Divider />
            <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase">Task Columns</Text>
            {taskCols.map(renderCol)}

            <Divider />
            <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase">Financial Columns</Text>
            {financeCols.map(renderCol)}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={resetDefaults}>Reset Defaults</Button>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
            onClick={handleSave} isLoading={saving}>Save &amp; Apply</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
